/**
 * HealthTopicsList — /health/diseases, /health/procedures, /health/drugs,
 * /health/wellness, /health/mental-health, /health/traditional-medicine.
 * Reused via `kind` prop.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, ShieldAlert } from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";
import SuggestTopicWidget from "@/components/SuggestTopicWidget";

const _kgSlug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function HealthTopicsList({ kind, label, description }) {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { http.get("/health/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = { kind, limit: 60 };
    if (q) p.q = q;
    if (category) p.category = category;
    http.get("/health/topics", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [kind, q, category]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const categoryOptions = kind === "disease"
    ? (meta?.disease_categories || [])
    : kind === "wellness"
      ? (meta?.wellness_categories || [])
      : kind === "traditional"
        ? (meta?.traditional_medicine || [])
        : [];

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid={`topics-list-${kind}`}>
        <div className="mb-4">
          <div className="overline mb-1">{label.toUpperCase()}</div>
          <h1 className="font-display text-4xl mb-1">{label}</h1>
          {description && <p className="opacity-70 max-w-3xl">{description}</p>}
        </div>
        <div className="text-xs opacity-70 inline-flex items-start gap-1.5 mb-6">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
          {meta?.disclaimer || "Educational only — not medical advice."}
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          {categoryOptions.length > 0 && (
            <select value={category} onChange={e => setFilter("category", e.target.value)}
              className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="filter-category">
              <option value="">All categories</option>
              {categoryOptions.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          )}
          <input value={q} onChange={e => setFilter("q", e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="text-sm px-2 py-1 rounded border flex-1 min-w-[10rem]"
            style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {/* AI-powered suggestion — diseases land on the canonical KG page */}
        {kind === "disease" ? (
          <SuggestTopicWidget
            entityType="disease"
            label="Can't find a condition? Suggest it — we'll write the article with AI"
            placeholder="e.g. Rheumatoid Arthritis, Type 1 Diabetes, Chikungunya"
          />
        ) : (
          <SuggestHealthTopicWidget kind={kind} label={label} />
        )}

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center opacity-70">
            No {label.toLowerCase()} yet. Try searching by name to auto-generate an AI article.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(t => (
              <Link key={t.slug}
                to={t.kind === "disease" ? `/disease/${_kgSlug(t.name)}` : `/health/topics/${t.kind}/${encodeURIComponent(t.name)}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`topic-card-${t.slug}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
                  {t.category || t.kind}
                </div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs opacity-70 mt-1 inline-flex items-center gap-1">
                  {t.status === "ready"
                    ? <><Sparkles className="w-3 h-3"/>Article ready</>
                    : t.status === "generating"
                      ? <><Loader2 className="w-3 h-3 animate-spin"/>Generating…</>
                      : <><Sparkles className="w-3 h-3"/>AI writes on first visit</>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Small AI-suggestion widget for non-disease Health topic kinds
 * (procedure, drug, wellness, mental, traditional, symptom). Kicks
 * off /api/health/topics/kick and navigates to the topic page.
 */
function SuggestHealthTopicWidget({ kind, label }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await http.post("/health/topics/kick", { kind, name: n });
      window.location.href = `/health/topics/${kind}/${encodeURIComponent(n)}`;
    } finally { setBusy(false); }
  };
  return (
    <section
      className="wofy-card !p-4 mb-8 border-2"
      style={{ borderColor: "var(--wofy-primary)", background: "rgba(178,93,56,0.04)" }}
      data-testid={`suggest-topic-${kind}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }}/>
        <div className="text-sm font-medium">Can't find a {label.toLowerCase().replace(/s$/, "")}? Suggest it — we'll write it with AI</div>
      </div>
      <div className="flex items-center gap-2 wofy-card !p-2">
        <input
          value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") go(); }}
          placeholder={`e.g. ${kind === "procedure" ? "Cataract Surgery" : kind === "drug" ? "Ashwagandha" : "Mediterranean Diet"}`}
          className="flex-1 bg-transparent outline-none text-sm px-2"
          data-testid="suggest-input"
        />
        <button onClick={go} disabled={!name.trim() || busy}
          className="wofy-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="suggest-generate">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
          {busy ? "Preparing…" : "Generate"}
        </button>
      </div>
    </section>
  );
}
